import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import {
  distinctUntilChanged,
  filter,
  Observable,
  of,
  Subject,
  Subscription,
  switchMap,
  takeUntil,
  tap,
  map,
  combineLatest,
} from 'rxjs';
import { selectSubscriptionPlans } from '../../../store/user/user.reducer';
import {
  IAddressInfo,
  ICreateOrderRequest,
  IFeatureType,
  IOrderInfo,
  ISubscriptionPlanInfo,
  ISubscriptionPlanWithTaxAndDiscountInfo,
  OrderStatus,
  PaymentStatus,
} from '../../../models/Subscription';
import {
  CreateAutoRenewOrderAction,
  CreateOrderAction,
  CreateOrderFailureAction,
  CreateOrderSuccessAction,
  FetchSubscriptionPlansAction,
  RefreshTokenSuccessAction,
  UpdateOrderAction,
  UpdateOrderSuccessAction,
  UpdateUserAction,
  UpdateUserSuccessAction,
} from '../../../store/user/user.actions';
import { FormValidators } from '../../../_helpers/form-validators';
import { AsyncState } from '../../../store';
import { Actions, ofType } from '@ngrx/effects';
import { SnackBarService } from '../../../services/snackbar.service';
import { AuthService } from '../../../services/auth.service';
import {
  CountriesStatesService,
  ICountryInfoWithStates,
} from '../../../services/countries_states_service';
import {
  PrivacyPolicyURL,
  RefundPolicyURL,
  TermsOfServiceURL,
} from '../../../../resources/common.resources';
import { UserService } from '../../../services/user.service';
import {
  areObjectsWithSameData,
  getCurrencySymbol,
} from '../../../_helpers/common.functions';
import { selectClientInfo } from '../../../store/auth/auth.reducer';
import { IClientInfo } from '../../../models/authentication';
import { SessionService } from '../../../services/session.service';
import {
  getDiscountedPercentage,
  getDiscountedPrice,
  formatAmount,
} from '../../../_helpers/common.functions';

@Component({
  selector: 'app-order',
  standalone: false,
  templateUrl: './order.component.html',
  styleUrl: './order.component.scss',
})
export class OrderComponent implements OnInit, OnDestroy {
  sameAsBilling = false;
  updateShippingAddressInProfile = false;
  subscriptionId: string | null = null;
  errorMessage: string | null = null;
  isFormChanged = false;
  initialFormValues: any = {}; // Store Initial Values
  selectedSubscription: ISubscriptionPlanWithTaxAndDiscountInfo | null = null;
  oldSubscriptionId: string | null = null;
  existingOrder: IOrderInfo | null = null;
  // Dropdown Options
  countriesOptions$: Observable<ICountryInfoWithStates[]>;
  billingStatesOptions: string[] = [];
  shippingStatesOptions: string[] = [];
  showGSTOptions: boolean = false;
  showSubscriptionAsDialog: boolean = false;

  subscriptionPlan: ISubscriptionPlanInfo | null = null;
  private _componentSubscriptions: Subscription[] = [];
  private destroy$ = new Subject<void>();
  clientInfo$: Observable<IClientInfo | null>;

  getCurrSymbol = getCurrencySymbol;
  formatAmount = formatAmount;

  orderState: AsyncState = {
    status: 'NotFetched',
  };
  serviceURLS = {
    termsOfServiceURL: TermsOfServiceURL,
    privacyPolicyURL: PrivacyPolicyURL,
    refundPolicyURL: RefundPolicyURL,
  };

  // Expose Imported Functions to Template
  public getDiscountedPercentage = getDiscountedPercentage;
  public getDiscountedPrice = getDiscountedPrice;

  constructor(
    private route: ActivatedRoute,
    private store: Store,
    private cdr: ChangeDetectorRef,
    private actions$: Actions,
    private router: Router,
    private snackBarService: SnackBarService,
    private authService: AuthService,
    private countriesStatesService: CountriesStatesService,
    private userService: UserService,
    private sessionService: SessionService,
  ) {
    // Access state during navigation
    const navigation = this.router.getCurrentNavigation();
    const state = navigation?.extras?.state;
    if (state && 'oldSubscriptionId' in state) {
      this.oldSubscriptionId =
        typeof state['oldSubscriptionId'] === 'string'
          ? state['oldSubscriptionId']
          : null;
    } else {
      this.oldSubscriptionId = null;
    }

    // get client info
    this.clientInfo$ = this.store.select(selectClientInfo);

    // get filtered country options as per current client country
    this.countriesOptions$ = combineLatest([
      this.clientInfo$,
      this.countriesStatesService.getAllCountries(),
    ]).pipe(
      map(([clientInfo, countries]) => {
        if (!clientInfo) return [];

        return countries.filter((c) => c.code === clientInfo.CountryCode);
      }),
    );

    const nav = this.router.getCurrentNavigation();
    this.showSubscriptionAsDialog =
      nav?.extras?.state?.['showSubscriptionAsDialog'] ?? true;
  }

  orderForm = new FormGroup({
    billingAddress: new FormGroup({
      line1: new FormControl(
        { value: '', disabled: false },
        FormValidators.addressLine1Validators(),
      ),
      line2: new FormControl(
        { value: '', disabled: false },
        FormValidators.addressLine2Validators(),
      ),
      city: new FormControl(
        { value: '', disabled: false },
        FormValidators.cityValidators(),
      ),
      postalCode: new FormControl(
        { value: '', disabled: false },
        FormValidators.postalCodeValidators(),
      ),
      country: new FormControl(
        { value: '', disabled: false },
        FormValidators.countryValidators(),
      ),
      state: new FormControl(
        { value: '', disabled: false },
        FormValidators.stateValidators(),
      ),
      gst: new FormControl(
        { value: '', disabled: false },
        FormValidators.gstValidators(),
      ),
    }),
    shippingAddress: new FormGroup({
      line1: new FormControl(
        { value: '', disabled: false },
        FormValidators.addressLine1Validators(),
      ),
      line2: new FormControl(
        { value: '', disabled: false },
        FormValidators.addressLine2Validators(),
      ),
      city: new FormControl(
        { value: '', disabled: false },
        FormValidators.cityValidators(),
      ),
      postalCode: new FormControl(
        { value: '', disabled: false },
        FormValidators.postalCodeValidators(),
      ),
      country: new FormControl(
        { value: '', disabled: false },
        FormValidators.countryValidators(),
      ),
      state: new FormControl(
        { value: '', disabled: false },
        FormValidators.stateValidators(),
      ),
    }),
    sameAsBillingCheckbox: new FormControl({ value: false, disabled: false }),
  });

  // pre fill address for existing order
  preFillOrderForm(order: IOrderInfo) {
    if (order) {
      let billingAddress = order.BillingAddress;
      let shippingAddress = order.ShippingAddress;

      const getRawAddress = (address: IAddressInfo) => {
        return {
          line1: address.Line1,
          line2: address.Line2 as string,
          city: address.City,
          postalCode: address.PostalCode,
          country: address.Country,
          state: address.State,
          gst: address.GSTIN as string,
        };
      };

      // pre-fill billing address
      const rawBillingAddress = getRawAddress(billingAddress);
      const formBillingAddress = this.orderForm.get('billingAddress')!;

      formBillingAddress.patchValue(rawBillingAddress);

      // pre-fill shipping address
      if (
        areObjectsWithSameData(billingAddress, shippingAddress, [
          'AddressId',
          'GSTIN',
        ])
      ) {
        this.orderForm.get('sameAsBillingCheckbox')?.setValue(true);
        this.onSameAsBillingChange();
      } else {
        const rawShippingAddress = getRawAddress(shippingAddress);
        const formShippingAddress = this.orderForm.get('shippingAddress')!;
        formShippingAddress.patchValue(rawShippingAddress);
      }
    }
  }

  // When User Clicks on Same As Billing Address CheckBox
  onSameAsBillingChange() {
    this.sameAsBilling = !this.sameAsBilling;
    const shippingAddress = this.orderForm.get('shippingAddress')!;
    const billingAddress = this.orderForm.get('billingAddress')!;

    if (this.sameAsBilling) {
      // Disable shipping address fields
      shippingAddress.disable();

      // Copy billing address to shipping address
      const billing = billingAddress.getRawValue();
      shippingAddress.patchValue(billing);

      // When patching the shipping address value, the country is changed, and which in turn enable the state dropdown,
      // therefore, explicitly disable the state field after paching values
      this.orderForm.get('shippingAddress.state')?.disable();

      // Subscribe to billing address changes
      this._componentSubscriptions.push(
        billingAddress.valueChanges
          .pipe(takeUntil(this.destroy$))
          .subscribe((billingValue) => {
            if (this.sameAsBilling) {
              shippingAddress.patchValue(billingValue);
              // To handle the case when country changes in the billing address
              this.orderForm.get('shippingAddress.state')?.disable();
            }
          }),
      );
    } else {
      // Enable shipping address fields
      shippingAddress.enable();
    }
  }

  // When User Clicks On "Update Shipping Address In Profile"
  onUpdateShippingAddressInProfile() {
    this.updateShippingAddressInProfile = !this.updateShippingAddressInProfile;
  }

  getDiscount(data: ISubscriptionPlanInfo): { text: string } | null {
    // Check the Features array for DiscountPercent or DiscountAmount
    const discountFeature = data.Features.find(
      (feature) =>
        feature.FeatureType === IFeatureType.DiscountPercent ||
        feature.FeatureType === IFeatureType.DiscountAmount,
    );

    // If no discount feature is found, return null
    if (!discountFeature || !discountFeature.Value) {
      return null;
    }

    // Handle DiscountPercent
    if (discountFeature.FeatureType === IFeatureType.DiscountPercent) {
      return { text: `Save ${discountFeature.Value}%` };
    }

    // Handle DiscountAmount
    if (discountFeature.FeatureType === IFeatureType.DiscountAmount) {
      return {
        text: `Save ${this.getCurrSymbol(data.Currency)} ${
          discountFeature.Value
        }`,
      };
    }

    return null;
  }

  // Check If Form Has Been Changed
  private hasFormChanged(): boolean {
    const currentValues: any = this.orderForm.getRawValue();
    return Object.keys(currentValues).some(
      (key) => currentValues[key] !== this.initialFormValues[key],
    );
  }

  onBackToSubscriptionsPageClick() {
    this.router.navigate(['subscriptions'], {
      queryParams: {
        tab: 'plans',
      },
      state: {
        showSubscriptionAsDialog: this.showSubscriptionAsDialog,
      },
    });
  }

  onSubcriptionPlansBtnClick() {
    this.router.navigate(['subscriptions'], {
      queryParams: {
        tab: 'plans',
      },
      state: {
        showSubscriptionAsDialog: this.showSubscriptionAsDialog,
      },
    });
  }

  formatAddressForProfileUpdate(addressData: any): string {
    // Extract address components with default empty strings
    const line1 = addressData.line1 || '';
    const line2 = addressData.line2 || '';
    const city = addressData.city || '';
    const postalCode = addressData.postalCode || '';
    const state = addressData.state || '';
    const country = addressData.country || '';

    const addressComponents = [
      line1 ? `${line1}` : '',
      line2 ? `${line2}` : '',
      city && postalCode ? `${city} - ${postalCode}` : city ? `${city}` : '',
      state && country ? `${state}, ${country}` : state ? `${state},` : country,
    ].filter((component) => component); // Remove empty components

    // Join components with newlines
    return addressComponents.join('\n');
  }

  onPaymentClick() {
    if (this.updateShippingAddressInProfile) {
      const shippingAddress = this.orderForm
        .get('shippingAddress')!
        .getRawValue();
      // Get the Formatted String
      const formattedString =
        this.formatAddressForProfileUpdate(shippingAddress);
      // Add Address Field In the Changed Fields
      const changedFields: any = {};
      changedFields['primaryAddress'] = shippingAddress;
      // Get Token For Getting "UserId"
      const tokenInfo = this.authService.getDecodedToken();
      if (tokenInfo) {
        this.store.dispatch(
          UpdateUserAction({
            request: changedFields,
            userId: tokenInfo['UserInfo']['UserId'],
          }),
        );
      }
    } else {
      // Directly Make the payment
      this.onCheckoutClick();
    }
  }

  onCheckoutClick() {
    if (this.selectedSubscription) {
      const tokenInfo = this.authService.getDecodedToken();
      let userFullName = '';
      if (
        tokenInfo &&
        tokenInfo['UserInfo'] &&
        tokenInfo['UserInfo']['Fullname']
      ) {
        userFullName = tokenInfo['UserInfo']['Fullname'];
      }

      const billingAddressRef = this.orderForm.get('billingAddress');
      const shippingAddressRef = this.orderForm.get('shippingAddress');
      const sameAsBillingCheckboxRef = this.orderForm.get(
        'sameAsBillingCheckbox',
      );

      const billingAddress = billingAddressRef!.getRawValue();
      const shippingAddress = shippingAddressRef!.getRawValue();

      if (this.existingOrder) {
        let updatedOrder = null;
        // update billing and shipping address
        if (
          billingAddressRef?.dirty ||
          shippingAddressRef?.dirty ||
          sameAsBillingCheckboxRef?.dirty
        ) {
          updatedOrder = {
            OrderId: this.existingOrder.OrderId,
            BillingAddress: {
              ...this.existingOrder.BillingAddress,
              Line1: billingAddress.line1,
              Line2: billingAddress.line2,
              City: billingAddress.city,
              State: billingAddress.state,
              PostalCode: billingAddress.postalCode,
              Country: billingAddress.country,
              GSTIN: billingAddress.gst,
              FullName: userFullName,
            },
            ShippingAddress: {
              ...this.existingOrder.ShippingAddress,
              Line1: shippingAddress.line1,
              Line2: shippingAddress.line2,
              City: shippingAddress.city,
              State: shippingAddress.state,
              PostalCode: shippingAddress.postalCode,
              Country: shippingAddress.country,
              FullName: userFullName,
            },
          };
        }

        // update address if needed, else navigate to Payment page
        if (updatedOrder) {
          // Dispatch the Update Order Action
          this.store.dispatch(UpdateOrderAction({ request: updatedOrder }));
          this.orderState = {
            status: 'Loading',
          };
        } else {
          this.router.navigate(['transactions'], {
            queryParams: {
              transactionId: this.existingOrder.OrderId,
            },
            state: {
              showSubscriptionAsDialog: this.showSubscriptionAsDialog,
            },
          });
        }
      } else {
        // Convert GST to UpperCase when creating Order
        const gstinUppercase = billingAddress.gst
          ? billingAddress.gst.trim().toUpperCase()
          : null;

        const newOrder: ICreateOrderRequest = {
          GenerateUPILink: false, // Generate the Payment Order Id always for Web
          SubscriptionPlanId: this.selectedSubscription.Id,
          Amount: this.selectedSubscription.Price,
          GST: this.selectedSubscription.GST,
          OtherTax: this.selectedSubscription.OtherTax,
          Discount: this.selectedSubscription.Discount,
          TotalAmount: this.selectedSubscription.FinalAmount,
          Currency: this.selectedSubscription.Currency,
          BillingAddress: {
            Line1: billingAddress.line1,
            Line2: billingAddress.line2,
            City: billingAddress.city,
            State: billingAddress.state,
            PostalCode: billingAddress.postalCode,
            Country: billingAddress.country,
            GSTIN: gstinUppercase,
            FullName: userFullName,
          },
          ShippingAddress: {
            Line1: shippingAddress.line1,
            Line2: shippingAddress.line2,
            City: shippingAddress.city,
            State: shippingAddress.state,
            PostalCode: shippingAddress.postalCode,
            Country: shippingAddress.country,
            FullName: userFullName,
          },
        };

        // In Case Of "Renew" Pass the Old SubscriptionId
        if (this.oldSubscriptionId) {
          newOrder.OldSubscriptionId = this.oldSubscriptionId;
        }

        //Dispatch the Create Order Action
        if (this.selectedSubscription.IsAutoRenewing) {
          newOrder.AutoRenewPlanId = this.selectedSubscription.AppPlanId;
          this.store.dispatch(
            CreateAutoRenewOrderAction({ request: newOrder }),
          );
        } else {
          this.store.dispatch(CreateOrderAction({ request: newOrder }));
        }
        this.orderState = {
          status: 'Loading',
        };
      }
    }
  }

  // An order still waiting for payment, which the user can go on with
  private isOrderOpen(order: IOrderInfo): boolean {
    const finishedStatuses: string[] = [
      OrderStatus.Completed,
      OrderStatus.Cancelled,
      OrderStatus.Returned,
    ];
    return (
      !finishedStatuses.includes(order.Status) &&
      order.PaymentStatus !== PaymentStatus.Paid
    );
  }

  // fetch the existing order, if any, and then prefill the form with existing values
  updateOrderForm(foundPlan: ISubscriptionPlanWithTaxAndDiscountInfo) {
    if (foundPlan.OrderId) {
      of(foundPlan.OrderId)
        .pipe(
          switchMap((id) => {
            return this.userService.getOrder(id);
          }),
          map((res) => res.Value),
          tap((order) => {
            if (order) {
              // Only an unfinished order is continued; a completed, cancelled or paid one
              // only lends its addresses, and submitting creates a new order.
              if (this.isOrderOpen(order)) {
                this.existingOrder = order;
              }
              this.preFillOrderForm(order);
            }
          }),
        )
        .subscribe();
    }
  }

  private readOldSubscriptionIdFromState(): void {
    const navState = history.state;
    this.oldSubscriptionId =
      navState && typeof navState['oldSubscriptionId'] === 'string'
        ? navState['oldSubscriptionId']
        : null;
  }

  ngOnInit(): void {
    this.readOldSubscriptionIdFromState();

    // Disable stats field initially (will be enabled when the country changes)
    this.orderForm.get('billingAddress.state')?.disable();
    this.orderForm.get('shippingAddress.state')?.disable();

    // Subscribe to "Billing Country" state changes
    const billingCountryControl = this.orderForm.get('billingAddress.country');
    if (billingCountryControl) {
      this._componentSubscriptions.push(
        billingCountryControl.valueChanges
          .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
          .subscribe((countryCode) => {
            if (countryCode) {
              // Enable state field and fetch states
              this.orderForm.get('billingAddress.state')?.enable();
              this.countriesStatesService
                .getStatesByCountry(countryCode)
                .subscribe((states) => {
                  this.billingStatesOptions = states;
                  // Reset state value if the country changes
                  this.orderForm.get('billingAddress.state')?.setValue('');
                  this.cdr.detectChanges();
                  // Show GST field only if the country is India(country code "IN")
                  this.showGSTOptions = countryCode === 'IN';
                });
            } else {
              // Disable state field and clear options if no country is selected
              this.orderForm.get('billingAddress.state')?.disable();
              this.billingStatesOptions = [];
              this.orderForm.get('billingAddress.state')?.setValue('');
              this.showGSTOptions = false;
              this.cdr.detectChanges();
            }
          }),
      );
    }

    // Subscribe to "Shipping Country" state changes
    const shippingCountryControl = this.orderForm.get(
      'shippingAddress.country',
    );
    if (shippingCountryControl) {
      this._componentSubscriptions.push(
        shippingCountryControl.valueChanges
          .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
          .subscribe((country) => {
            if (country) {
              // Enable state field and fetch states
              this.orderForm.get('shippingAddress.state')?.enable();
              this.countriesStatesService
                .getStatesByCountry(country)
                .subscribe((states) => {
                  this.shippingStatesOptions = states;
                  // Reset state value if the country changes
                  this.orderForm.get('shippingAddress.state')?.setValue('');
                  this.cdr.detectChanges();
                });
            } else {
              // Disable state field and clear options if no country is selected
              this.orderForm.get('shippingAddress.state')?.disable();
              this.shippingStatesOptions = [];
              this.orderForm.get('shippingAddress.state')?.setValue('');
              this.cdr.detectChanges();
            }
          }),
      );
    }

    this._componentSubscriptions.push(
      this.route.queryParams
        .pipe(
          tap((params) => {
            this.subscriptionId = params['subscriptionId'] || null;
            if (!this.subscriptionId) {
              this.errorMessage = 'No subscription ID provided';
            }
          }),
        )
        .subscribe(() => {
          if (this.subscriptionId) {
            // Subscribe to subscription plans from the store
            this._componentSubscriptions.push(
              this.store
                .select(selectSubscriptionPlans)
                .subscribe((subscriptions) => {
                  if (subscriptions.state.status === 'Success') {
                    // Plans exist, check for the subscription ID
                    const foundPlan = subscriptions.plans.find(
                      (plan) => plan.Id === this.subscriptionId,
                    );
                    if (foundPlan) {
                      // Subscription ID found, update form and order
                      this.selectedSubscription = foundPlan;
                      this.subscriptionPlan = foundPlan;
                      this.errorMessage = null;

                      // Store Initial Values
                      this.initialFormValues = this.orderForm.getRawValue();
                      // do not update from if old subscription id found, as user is renewing subscription, so proceed to create new order
                      if (!this.oldSubscriptionId) {
                        this.updateOrderForm(foundPlan);
                      }
                    } else {
                      // Plans exist, but subscription ID not found
                      this.selectedSubscription = null;
                      this.errorMessage = `Subscription Plan Not Found.`;
                    }
                  } else if (subscriptions.state.status === 'NotFetched') {
                    // Plans not in store, dispatch action to fetch
                    this.store.dispatch(FetchSubscriptionPlansAction());
                  } else if (subscriptions.state.status === 'Loading') {
                    this.errorMessage = null;
                  } else if (subscriptions.state.status === 'Failure') {
                    this.selectedSubscription = null;
                    this.errorMessage = 'Failed To Load Subscription Plans.';
                  }
                }),
            );
          }
        }),
    );

    // Detect form changes, avoiding control state changes
    this._componentSubscriptions.push(
      this.orderForm.valueChanges
        .pipe(
          distinctUntilChanged(
            (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr),
          ), // Prevent emission on same values
        )
        .subscribe((currentValues) => {
          this.isFormChanged = this.hasFormChanged();
          this.cdr.detectChanges();
        }),
    );

    // Handle Update Order Success
    this._componentSubscriptions.push(
      this.actions$
        .pipe(ofType(UpdateOrderSuccessAction), takeUntil(this.destroy$))
        .subscribe(({ response }) => {
          this.orderState = {
            status: 'Success',
          };
          this.router.navigate(['transactions'], {
            queryParams: {
              transactionId: response.Value.OrderId,
            },
            state: {
              showSubscriptionAsDialog: this.showSubscriptionAsDialog,
            },
          });
        }),
    );

    // Handle Create Order Success
    this._componentSubscriptions.push(
      this.actions$
        .pipe(ofType(CreateOrderSuccessAction), takeUntil(this.destroy$))
        .subscribe(({ response }) => {
          this.orderState = {
            status: 'Success',
          };
          this.router.navigate(['transactions'], {
            queryParams: {
              transactionId: response.Value.OrderId,
            },
            state: {
              showSubscriptionAsDialog: this.showSubscriptionAsDialog,
            },
          });
        }),
    );

    // Handle Create Order Failure
    this._componentSubscriptions.push(
      this.actions$
        .pipe(ofType(CreateOrderFailureAction), takeUntil(this.destroy$))
        .subscribe(({ error }) => {
          this.orderState = {
            status: 'Failure',
          };
          // this.snackBarService.showSnackBar('error', "Error Creating Order. Please try again.")
        }),
    );

    // When Profile Is Updated, RefreshToken will be updated and then we proceed further
    // Handle Refresh Token Success
    this._componentSubscriptions.push(
      this.actions$
        .pipe(ofType(RefreshTokenSuccessAction), takeUntil(this.destroy$))
        .subscribe(async ({ response }) => {
          // Again Initialize the Session Token When Token Is Refreshed
          await this.sessionService.initializeApp();
          // When Address Is Updated SuccessFull, make the payment
          this.onCheckoutClick();
        }),
    );

    this._componentSubscriptions.push(
      // Subscribe to router events to handle navigation changes
      this.router.events
        .pipe(
          filter((event) => event instanceof NavigationEnd),
          takeUntil(this.destroy$),
        )
        .subscribe(() => {
          // Access state during navigation
          const navigation = this.router.getCurrentNavigation();
          const state = navigation?.extras?.state;
          if (state && 'oldSubscriptionId' in state) {
            this.oldSubscriptionId =
              typeof state['oldSubscriptionId'] === 'string'
                ? state['oldSubscriptionId']
                : null;
          } else {
            this.oldSubscriptionId = null;
          }
        }),
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this._componentSubscriptions.forEach((sub) => sub.unsubscribe());
  }
}
